<script setup>
import { ref, computed } from 'vue'
import { useDropZone } from '@vueuse/core'
import { useTeacherDocumentUpload } from '@/composables/useTeacherDocumentUpload'
import InputError from '@/Components/Forms/InputError.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Required Documents'
  },
  description: {
    type: String,
    default: 'Please upload the following documents to complete your registration. All documents are mandatory.'
  },
  showShadow: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

// Setup document upload composable
const { uploadDocument, getDownloadUrl, deleteDocument } = useTeacherDocumentUpload()

// File input refs and drop zone refs
const dbsFileInput = ref(null)
const identityFileInput = ref(null)
const dbsDropZoneRef = ref(null)
const identityDropZoneRef = ref(null)

// Upload states
const dbsUploading = ref(false)
const identityUploading = ref(false)
const dbsProgress = ref(0)
const identityProgress = ref(0)

/**
 * Failures are shown against the document they belong to.
 *
 * They used to be raised with `alert()`, which put the message in a browser
 * dialog over the page — it had to be dismissed before the form was usable
 * again, and once dismissed nothing was left to say which document had failed.
 * The application form next door reports them the same way.
 */
const dbsError = ref('')
const identityError = ref('')

// Computed properties
const dbsDocument = computed(() => props.modelValue.dbs_document)
const identityDocument = computed(() => props.modelValue.identity_document)

// Validate file helper
const validateFile = (file) => {
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload PDF, JPG, JPEG, or PNG files only.')
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB')
  }
}

// Process DBS file upload
const processDbsUpload = async (file) => {
  if (!file) return

  try {
    dbsError.value = ''
    validateFile(file)

    dbsUploading.value = true
    dbsProgress.value = 0

    // If user already has a document, delete it from server first
    if (dbsDocument.value?.path) {
      try {
        await deleteDocument(dbsDocument.value.path, 'dbs')
      } catch (e) {
        // Ignore deletion errors - file may not exist
        console.warn('Failed to delete old DBS document:', e)
      }
    }

    const result = await uploadDocument(file, 'dbs', {
      onSuccess: () => {
        dbsProgress.value = 100
      }
    })

    emit('update:modelValue', {
      ...props.modelValue,
      dbs_document: {
        path: result.path,
        original_name: result.original_name,
        size: result.size,
        type: 'dbs'
      }
    })

  } catch (error) {
    dbsError.value = error.message || 'Upload failed. Please try again.'
  } finally {
    dbsUploading.value = false
    if (dbsFileInput.value) {
      dbsFileInput.value.value = ''
    }
  }
}

// Handle DBS upload from file input
const handleDbsUpload = async (event) => {
  const file = event.target.files[0]
  await processDbsUpload(file)
}

// Process Identity file upload
const processIdentityUpload = async (file) => {
  if (!file) return

  try {
    identityError.value = ''
    validateFile(file)

    identityUploading.value = true
    identityProgress.value = 0

    // If user already has a document, delete it from server first
    if (identityDocument.value?.path) {
      try {
        await deleteDocument(identityDocument.value.path, 'identity')
      } catch (e) {
        // Ignore deletion errors - file may not exist
        console.warn('Failed to delete old identity document:', e)
      }
    }

    const result = await uploadDocument(file, 'identity', {
      onSuccess: () => {
        identityProgress.value = 100
      }
    })

    emit('update:modelValue', {
      ...props.modelValue,
      identity_document: {
        path: result.path,
        original_name: result.original_name,
        size: result.size,
        type: 'identity'
      }
    })

  } catch (error) {
    identityError.value = error.message || 'Upload failed. Please try again.'
  } finally {
    identityUploading.value = false
    if (identityFileInput.value) {
      identityFileInput.value.value = ''
    }
  }
}

// Handle Identity upload from file input
const handleIdentityUpload = async (event) => {
  const file = event.target.files[0]
  await processIdentityUpload(file)
}

// Download document
const downloadDocument = async (doc) => {
  try {
    const { download_url } = await getDownloadUrl(doc.path, doc.type)

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a')
    link.href = download_url
    link.download = doc.original_name || doc.filename
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    const message = error.message || 'Failed to open document'

    if (doc.type === 'dbs') {
      dbsError.value = message
    } else {
      identityError.value = message
    }
  }
}

// Remove document - just toggle back to uploader without deleting from server
const removeDbsDocument = () => {
  if (!dbsDocument.value) return

  // Just clear from UI to show uploader again
  emit('update:modelValue', {
    ...props.modelValue,
    dbs_document: null
  })
}

const removeIdentityDocument = () => {
  if (!identityDocument.value) return

  // Just clear from UI to show uploader again
  emit('update:modelValue', {
    ...props.modelValue,
    identity_document: null
  })
}

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// Trigger file input
const triggerDbsUpload = () => {
  if (!dbsUploading.value) {
    dbsFileInput.value?.click()
  }
}

const triggerIdentityUpload = () => {
  if (!identityUploading.value) {
    identityFileInput.value?.click()
  }
}

// Setup drop zones
const onDbsDrop = (files) => {
  if (files && files.length > 0 && !dbsUploading.value) {
    processDbsUpload(files[0])
  }
}

const onIdentityDrop = (files) => {
  if (files && files.length > 0 && !identityUploading.value) {
    processIdentityUpload(files[0])
  }
}

// Initialize drop zones
const { isOverDropZone: isOverDbsZone } = useDropZone(dbsDropZoneRef, {
  onDrop: onDbsDrop,
  dataTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
})

const { isOverDropZone: isOverIdentityZone } = useDropZone(identityDropZoneRef, {
  onDrop: onIdentityDrop,
  dataTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
})
</script>

<template>
  <section :class="['bg-white rounded-2xl p-5 md:p-10', showShadow ? 'shadow-sm border border-gray-100' : '']">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2 flex items-center">
        <span class="material-symbols-outlined text-brand-blue mr-3">upload_file</span>
        {{ title }}
      </h2>
      <p class="text-gray-600">{{ description }}</p>
    </div>

    <div class="space-y-6">
      <!-- DBS Upload Field -->
      <div id="dbs-upload-field" class="form-group">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Enhanced DBS Check Certificate
          <span class="text-red-500 ml-1">*</span>
        </label>

        <!-- Upload Area (when no document) -->
        <div
          v-if="!dbsDocument"
          ref="dbsDropZoneRef"
          id="dbs-upload-area"
          @click="triggerDbsUpload"
          class="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-blue transition-all cursor-pointer bg-gray-50 hover:bg-blue-50"
          :class="{
            'opacity-50 cursor-not-allowed': dbsUploading,
            'border-brand-blue bg-blue-100 scale-[1.02]': isOverDbsZone
          }"
        >
          <input
            ref="dbsFileInput"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            class="hidden"
            @change="handleDbsUpload"
            :disabled="dbsUploading"
          />

          <div v-if="!dbsUploading">
            <span class="material-symbols-outlined text-6xl mb-4 block transition-colors" :class="isOverDbsZone ? 'text-brand-blue' : 'text-gray-400'">cloud_upload</span>
            <p class="text-gray-700 font-semibold mb-2" :class="{ 'text-brand-blue': isOverDbsZone }">
              {{ isOverDbsZone ? 'Drop your file here' : 'Click or drag & drop your DBS certificate' }}
            </p>
            <p class="text-sm text-gray-500">PDF, JPG, JPEG, or PNG (Max 10MB)</p>
          </div>

          <div v-else>
            <span class="material-symbols-outlined text-6xl text-brand-blue mb-4 block animate-pulse">upload</span>
            <p class="text-brand-blue font-semibold mb-2">Uploading... {{ dbsProgress }}%</p>
            <div class="w-full bg-gray-200 rounded-full h-2 mt-4">
              <div class="bg-brand-blue h-2 rounded-full transition-all" :style="{ width: `${dbsProgress}%` }"></div>
            </div>
          </div>
        </div>

        <!-- Document Preview (when uploaded) -->
        <div v-else class="border-2 border-green-300 bg-green-50 rounded-xl p-6">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div class="flex items-center space-x-4 min-w-0 flex-1">
              <div class="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white text-2xl">check_circle</span>
              </div>
              <div class="min-w-0">
                <p class="font-semibold text-gray-900 truncate">{{ dbsDocument.original_name }}</p>
                <p class="text-sm text-gray-600">{{ formatFileSize(dbsDocument.size) }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2 flex-shrink-0">
              <button
                type="button"
                @click="downloadDocument(dbsDocument)"
                class="px-4 py-2 bg-white text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined text-xl">download</span>
              </button>
              <button
                type="button"
                @click="removeDbsDocument"
                class="px-4 py-2 bg-white text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined text-xl">delete</span>
              </button>
            </div>
          </div>
        </div>

        <div class="mt-3 flex items-start space-x-2">
          <span class="material-symbols-outlined text-blue-500 mt-0.5 text-sm">info</span>
          <p class="text-xs text-gray-600 leading-relaxed">Your DBS certificate must be an Enhanced DBS check and dated within the last 3 years. We accept Update Service certificates.</p>
        </div>
        <InputError :message="dbsError || errors.dbs_file || errors.dbs_document" />
      </div>

      <!-- Identity Upload Field -->
      <div id="identity-upload-field" class="form-group">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Identity Verification Document
          <span class="text-red-500 ml-1">*</span>
        </label>

        <!-- Upload Area (when no document) -->
        <div
          v-if="!identityDocument"
          ref="identityDropZoneRef"
          id="identity-upload-area"
          @click="triggerIdentityUpload"
          class="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-blue transition-all cursor-pointer bg-gray-50 hover:bg-blue-50"
          :class="{
            'opacity-50 cursor-not-allowed': identityUploading,
            'border-brand-blue bg-blue-100 scale-[1.02]': isOverIdentityZone
          }"
        >
          <input
            ref="identityFileInput"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            class="hidden"
            @change="handleIdentityUpload"
            :disabled="identityUploading"
          />

          <div v-if="!identityUploading">
            <span class="material-symbols-outlined text-6xl mb-4 block transition-colors" :class="isOverIdentityZone ? 'text-brand-blue' : 'text-gray-400'">badge</span>
            <p class="text-gray-700 font-semibold mb-2" :class="{ 'text-brand-blue': isOverIdentityZone }">
              {{ isOverIdentityZone ? 'Drop your file here' : 'Click or drag & drop your identity document' }}
            </p>
            <p class="text-sm text-gray-500">PDF, JPG, JPEG, or PNG (Max 10MB)</p>
          </div>

          <div v-else>
            <span class="material-symbols-outlined text-6xl text-brand-blue mb-4 block animate-pulse">upload</span>
            <p class="text-brand-blue font-semibold mb-2">Uploading... {{ identityProgress }}%</p>
            <div class="w-full bg-gray-200 rounded-full h-2 mt-4">
              <div class="bg-brand-blue h-2 rounded-full transition-all" :style="{ width: `${identityProgress}%` }"></div>
            </div>
          </div>
        </div>

        <!-- Document Preview (when uploaded) -->
        <div v-else class="border-2 border-green-300 bg-green-50 rounded-xl p-6">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div class="flex items-center space-x-4 min-w-0 flex-1">
              <div class="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white text-2xl">check_circle</span>
              </div>
              <div class="min-w-0">
                <p class="font-semibold text-gray-900 truncate">{{ identityDocument.original_name }}</p>
                <p class="text-sm text-gray-600">{{ formatFileSize(identityDocument.size) }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2 flex-shrink-0">
              <button
                type="button"
                @click="downloadDocument(identityDocument)"
                class="px-4 py-2 bg-white text-brand-blue border border-brand-blue rounded-lg hover:bg-blue-50 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined text-xl">download</span>
              </button>
              <button
                type="button"
                @click="removeIdentityDocument"
                class="px-4 py-2 bg-white text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined text-xl">delete</span>
              </button>
            </div>
          </div>
        </div>

        <div class="mt-3 flex items-start space-x-2">
          <span class="material-symbols-outlined text-green-500 mt-0.5 text-sm">info</span>
          <p class="text-xs text-gray-600 leading-relaxed">Please upload a valid government-issued photo ID such as a passport, driving license, or national identity card. The document must be in date and clearly show your photo and personal details.</p>
        </div>
        <InputError :message="identityError || errors.identity_file || errors.identity_document" />
      </div>
    </div>
  </section>
</template>
